// src/components/world_explorer/WorldStructureCard.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Language as GalaxyIcon,
  Public as PlanetIcon,
  Science as StarIcon,
  Groups as RaceIcon,
  Flag as FactionIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

// Кастомные иконки для лучшей визуализации
const EntityIcons = {
  galaxy: GalaxyIcon,
  star: StarIcon,
  planet: PlanetIcon,
  race: RaceIcon,
  faction: FactionIcon
};

const WorldStructureCard = ({ 
  worldData, 
  galaxies = [], 
  planets = [], 
  stars = [], 
  races = [], 
  factions = [],
  environments = [],
  onEntityOpen 
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Категории для фильтрации
  const categories = useMemo(() => [
    {
      id: 'all',
      label: 'All Entities',
      icon: CategoryIcon,
      color: 'primary',
      count: galaxies.length + stars.length + planets.length + races.length + factions.length
    },
    {
      id: 'galaxies',
      label: 'Galaxies',
      icon: GalaxyIcon,
      color: 'primary',
      count: galaxies.length
    },
    {
      id: 'stars',
      label: 'Stars',
      icon: StarIcon,
      color: 'warning',
      count: stars.length
    },
    {
      id: 'planets',
      label: 'Planets',
      icon: PlanetIcon,
      color: 'success',
      count: planets.length
    },
    {
      id: 'races',
      label: 'Races',
      icon: RaceIcon,
      color: 'info',
      count: races.length
    },
    {
      id: 'factions',
      label: 'Factions',
      icon: FactionIcon,
      color: 'secondary',
      count: factions.length
    }
  ], [galaxies, stars, planets, races, factions]);

  // Объединенные данные для поиска и фильтрации
  const allEntities = useMemo(() => [
    ...galaxies.map(g => ({ ...g, entityType: 'galaxy', category: 'galaxies' })),
    ...stars.map(s => ({ ...s, entityType: 'star', category: 'stars' })),
    ...planets.map(p => ({ ...p, entityType: 'planet', category: 'planets' })),
    ...races.map(r => ({ ...r, entityType: 'race', category: 'races' })),
    ...factions.map(f => ({ ...f, entityType: 'faction', category: 'factions' }))
  ], [galaxies, stars, planets, races, factions]);

  // Отфильтрованные данные
  const filteredEntities = useMemo(() => {
    let filtered = allEntities;

    // Фильтрация по поисковому запросу
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(entity => 
        entity.name?.toLowerCase().includes(term) ||
        entity.type?.toLowerCase().includes(term) ||
        entity.description?.toLowerCase().includes(term) ||
        entity.id?.toLowerCase().includes(term)
      );
    }

    // Фильтрация по категории
    if (activeCategory !== 'all') {
      filtered = filtered.filter(entity => entity.category === activeCategory);
    }

    // Сортировка по имени
    filtered = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return filtered;
  }, [allEntities, searchTerm, activeCategory]);

  // Группировка по категориям для отображения
  const groupedEntities = useMemo(() => {
    const groups = {};
    filteredEntities.forEach(entity => {
      if (!groups[entity.category]) {
        groups[entity.category] = [];
      }
      groups[entity.category].push(entity);
    });
    return groups;
  }, [filteredEntities]);

  const getEntityDisplayName = (entity) => {
    return entity.name || entity.planet_id || entity.galaxy_id || entity.star_id || `Unnamed ${entity.entityType}`;
  };

  const getEntityDescription = (entity) => {
    switch (entity.entityType) {
      case 'planet':
        return `${entity.type || 'Planet'} • ${entity.system_id || 'Unknown system'}`;
      case 'star':
        return `${entity.spectral_class || entity.type || 'Star'} • ${entity.galaxy_id || 'Unknown galaxy'}`;
      case 'galaxy':
        return `${entity.type || 'Galaxy'} • ${entity.diameter_ly ? `${entity.diameter_ly} ly` : ''}`;
      case 'race':
        return entity.description || 'Biological species';
      case 'faction':
        return entity.description || 'Political organization';
      default:
        return entity.type || entity.entityType;
    }
  };

  const getColorValue = (colorName) => {
    const colorMap = {
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      error: theme.palette.error.main
    };
    return colorMap[colorName] || theme.palette.primary.main;
  };

  const EntityCard = ({ entity }) => {
    const IconComponent = EntityIcons[entity.entityType] || CategoryIcon;
    const category = categories.find(cat => cat.id === entity.category);
    const colorValue = getColorValue(category?.color);
    
    return (
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 1,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 2,
            borderColor: colorValue
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: theme.palette.mode === 'light' 
                  ? `${colorValue}20`  // 20% opacity in light mode
                  : `${colorValue}30`, // 30% opacity in dark mode
                color: colorValue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 40
              }}
            >
              <IconComponent fontSize="small" />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="subtitle2" 
                component="div" 
                sx={{ 
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {getEntityDisplayName(entity)}
              </Typography>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {getEntityDescription(entity)}
              </Typography>

              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {entity.temperature_c !== undefined && (
                  <Chip 
                    label={`${entity.temperature_c}°C`} 
                    size="small" 
                    variant="outlined"
                  />
                )}

                {entity.gravity && (
                  <Chip 
                    label={`${entity.gravity}g`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>

            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => onEntityOpen(entity, entity.entityType)}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              View
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Заголовок и описание */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {worldData.name || 'World Explorer'}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {worldData.description || 'Explore the complete structure and all entities of this world in one comprehensive view.'}
        </Typography>
      </Box>

      {/* Панель управления */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search entities by name, type, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {categories.map((category) => (
                  <Chip
                    key={category.id}
                    icon={<category.icon />}
                    label={`${category.label} (${category.count})`}
                    onClick={() => setActiveCategory(category.id)}
                    color={activeCategory === category.id ? category.color : 'default'}
                    variant={activeCategory === category.id ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Статистика */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📊 World Statistics
          </Typography>
          <Grid container spacing={1}>
            {categories.filter(cat => cat.id !== 'all').map((category) => {
              const colorValue = getColorValue(category.color);
              return (
                <Grid item xs={6} sm={4} md={2} key={category.id}>
                  <Box
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 1,
                      bgcolor: theme.palette.mode === 'light' 
                        ? `${colorValue}15`  // 15% opacity in light mode
                        : `${colorValue}20`, // 20% opacity in dark mode
                      border: `1px solid ${theme.palette.mode === 'light' 
                        ? `${colorValue}30`  // 30% opacity in light mode
                        : `${colorValue}40`  // 40% opacity in dark mode
                      }`
                    }}
                  >
                    <category.icon 
                      sx={{ 
                        fontSize: 24, 
                        color: colorValue,
                        mb: 1 
                      }} 
                    />
                    <Typography variant="h6" fontWeight="bold" color={colorValue}>
                      {category.count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.label}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Результаты поиска */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {searchTerm || activeCategory !== 'all' ? 'Search Results' : 'All World Entities'}
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            {filteredEntities.length} entities found
          </Typography>
        </Box>

        {filteredEntities.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No entities found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm 
                  ? `No entities match "${searchTerm}". Try a different search term.`
                  : 'No entities available in the current filters.'
                }
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {/* Компактный вид для большого количества элементов */}
            {filteredEntities.length > 10 ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {filteredEntities.map((entity, index) => (
                    <EntityCard key={`${entity.entityType}-${entity.id}-${index}`} entity={entity} />
                  ))}
                </Box>
              </Grid>
            ) : (
              // Группированный вид для малого количества элементов
              Object.entries(groupedEntities).map(([category, entities]) => {
                const categoryInfo = categories.find(cat => cat.id === category);
                const colorValue = getColorValue(categoryInfo?.color);
                
                return (
                  <Grid item xs={12} md={6} key={category}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <categoryInfo.icon sx={{ color: colorValue }} />
                          <Typography variant="h6" component="div">
                            {categoryInfo.label}
                          </Typography>
                          <Chip 
                            label={entities.length} 
                            size="small" 
                            color={categoryInfo.color}
                            variant="outlined"
                          />
                        </Box>
                        
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          {entities.map((entity, index) => (
                            <EntityCard key={`${entity.entityType}-${entity.id}-${index}`} entity={entity} />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        )}
      </Box>

      {/* Информация о мире */}
      {worldData.metadata && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              World loaded: {new Date(worldData.metadata.loadedAt).toLocaleString()}
              {worldData.metadata.worldDetails?.created_at && (
                <> • Created: {new Date(worldData.metadata.worldDetails.created_at).toLocaleDateString()}</>
              )}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default WorldStructureCard;